import { useState, type FormEvent } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SendRounded from "@mui/icons-material/SendRounded";
import MoreHorizRounded from "@mui/icons-material/MoreHorizRounded";
import { Avatar as PlayerAvatar } from "../../../common/components/Avatar";
import { useMyProfile } from "../../../common/query/useMyProfile";
import { ThumbUpOutlineIcon as ThumbUpAltOutlinedIcon } from "../components/ThumbUpOutlineIcon";
import ThumbUpAltRoundedIcon from "@mui/icons-material/ThumbUpAltRounded";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import { renderMarkdown } from "@bw/shared/utils";
import {
  playerHandleSuffix,
  actionCountLabel,
  authorInitials,
  authorLabel,
  countLabel,
  MISSING,
  socialTimeLabel,
} from "@bw/shared/components";
import type { CommentResponse } from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import { DetailHeader } from "../../../common/components/DetailHeader";
import { usePost } from "../hooks/usePost";
import { usePostComments } from "../hooks/usePostComments";
import { useCreateComment } from "../hooks/useCreateComment";
import { useLikeComment } from "../hooks/useLikeComment";
import { PostCardSkeleton } from "../components/PostCardSkeleton";
import { CommentSkeleton } from "../components/CommentSkeleton";
import { PostActions } from "../components/PostActions";
import { PostMenu } from "../components/PostMenu";
import { MaterialSocialPostMedia } from "../../../common/components/MaterialSocialPostCard";
import { hasSafeAvatar } from "../../../common/avatar";
import { ReportDialog } from "../../../common/components/ReportDialog";
import { GuardianAwareError } from "../../../common/guardian/GuardianAwareError";

/**
 * A post's detail surface (/community/posts/:postId). usePost loads the post →
 * author by display_name (NEVER the raw author_id), content as plain text, the
 * server's score / comment_count. usePostComments loads the thread → each comment
 * renders its author + content + time, all server values.
 *
 * Slice 3 (WRITE) adds participation:
 *   - a COMMENT box (a textarea + Submit). Content is required + trimmed — an
 *     empty / whitespace body blocks submit (useCreateComment is never called).
 *     Submit sends `createComment(postId, { content })` (a TOP-LEVEL comment this
 *     slice — parent_id omitted) and INVALIDATES the thread; the new comment
 *     arrives via REFETCH, never an optimistic phantom row. A failed create
 *     surfaces a role="alert" with the typed content RETAINED and no phantom row.
 *   - the POST's engagement is the shared LIKE model — a `<PostActions hideComment>`
 *     bar (Like + Repost), the SAME affordance the friends feed renders, so the
 *     post reads one way everywhere ("Like everywhere"). The post-level upvote /
 *     downvote was retired here. `hideComment` drops the Comment link — the thread
 *     + its "N comments" count already live on this page.
 *   - each COMMENT carries a first-class LIKE button (#1836) — the SAME model as the
 *     post ("Like everywhere"); the ▲ upvote / ▼ downvote was retired. Its
 *     aria-pressed mirrors the server's `liked_by_me`, the count is the server's
 *     `like_count`, and it calls `likeComment(id)` / `unlikeComment(id)` and
 *     INVALIDATES the comment thread; the displayed count moves ONLY to the refetched
 *     server value — there is NO optimistic +1. A failed like surfaces a role="alert"
 *     with the count UNCHANGED. Each comment mounts its OWN like mutation, so a like
 *     in flight disables ONLY that comment's button (per-comment busy scoping) and a
 *     double-click fires the mutation once.
 *
 * States: loading → role="status"; a failed post load (404 / reject) → an honest
 * error with a back-link to /community (the page is gated on the POST, not the
 * comments — a post is the page); empty thread → honest "No comments yet". No raw
 * uuid (post_id / author_id / comment_id) is ever rendered as visible text.
 *
 * The AppShell supplies no page title; this routed page OWNS its single <h1> as
 * the author display name inside its LinkedIn-style author header (avatar + name
 * + community·date byline), mirroring the feed card (PostCard's AuthorRow). It is
 * therefore NOT passed to <Page title={…}> — the page renders no second heading,
 * so there is exactly one <h1> and the author name appears once (WCAG 1.3.1 /
 * 2.4.6 — exactly one h1 per page).
 */
export function PostDetailPage() {
  const { postId } = useParams<{ postId: string }>();
  const postQ = usePost(postId);
  const commentsQ = usePostComments(postId);
  // Each COMMENT owns its own Like mutation (per-comment busy scoping) — see
  // CommentLikeButton below. The post itself uses the shared Like model (the
  // PostActions bar). There is no page-level engagement mutation here anymore.

  // ---- load states (gated on the POST — a post IS the page) ---------------
  if (postQ.isLoading) {
    return (
      <Page title="Post">
        <Box role="status" aria-label="Loading post">
          <PostCardSkeleton />
        </Box>
      </Page>
    );
  }

  if (postQ.isError || !postQ.data) {
    return (
      <Page title="Post">
        <Alert
          severity="error"
          role="alert"
          action={
            <Button
              component={RouterLink}
              to="/community"
              color="inherit"
              size="small"
            >
              Back to communities
            </Button>
          }
        >
          We couldn&apos;t load this post. It may not exist.
        </Alert>
      </Page>
    );
  }

  const post = postQ.data;
  // RELATIVE on the detail byline (U9) — "3h" / "2d", an absolute UTC date for
  // posts >= 7 days old. Shared with the feed card AND each comment (socialTimeLabel)
  // so a post never reads a different format — or a later calendar day — than its
  // own comments. The raw ISO is kept in the <time dateTime> attribute below.
  const time = socialTimeLabel(post.created_at);
  const authorName = authorLabel(post.author_display_name, post.author_id);
  const authorAvatarUrl = hasSafeAvatar(post.author_avatar_url)
    ? post.author_avatar_url
    : undefined;

  return (
    <Page narrow>
      <DetailHeader
        back={
          post.community_id
            ? { to: `/community/communities/${post.community_id}`, label: "Back to community" }
            : { to: "/community", label: "Back to communities" }
        }
        title="Post"
        titleComponent="p"
        subtitle={post.community_name ?? undefined}
      />
      <Box component="article" sx={{ px: { xs: 0.5, sm: 0 } }}>
        <Stack spacing={1.5}>
          {/* Author header mirrors the feed card: the saved profile photo when
            the server supplies a safe URL, otherwise initials. aria-hidden so
            the adjacent author name remains the single identity label. */}
          <Stack
            component="header"
            direction="row"
            spacing={1.5}
            sx={{ alignItems: "center" }}
          >
            <Avatar src={authorAvatarUrl} alt="" aria-hidden sx={{ width: 40, height: 40, fontSize: "0.875rem", fontWeight: 700 }}>
              {authorAvatarUrl
                ? null
                : authorInitials(post.author_display_name)}
            </Avatar>
            <Stack direction="row" sx={{ flex: 1, minWidth: 0, alignItems: "baseline", flexWrap: "wrap", columnGap: 0.5 }}>
              {/* The post author <h1> + the `#abcd` handle (last-4 of author_id —
                the same identity the feed card / leaderboard show). The handle
                rides a RESOLVED name only; an unresolved author is the em-dash
                placeholder with NO handle (the raw uid never leaks). The disc is
                aria-hidden: this h1 is the page's SINGLE landmark heading (WCAG
                2.4.6), so its accessible name stays the clean author name — the
                disc is a visual-only disambiguator, redundant for a screen reader
                that already reads the full name. */}
              <Typography component="h1" sx={{ fontSize: "0.9375rem", fontWeight: 700, m: 0 }}>
                <Typography component="span" sx={{ font: "inherit" }}>{authorName}</Typography>
                {authorName !== MISSING ? (
                  <Typography
                    component="span"
                    color="text.secondary"
                    aria-hidden
                    sx={{ fontSize: "0.8125rem", fontWeight: 600 }}
                  >
                    {` ${playerHandleSuffix(post.author_id)}`}
                  </Typography>
                ) : null}
              </Typography>
              <Typography
                component="p"
                variant="body2"
                color="text.secondary"
                sx={{ m: 0 }}
              >
                {time ? (
                  <>
                    <span aria-hidden="true">· </span>
                    <time dateTime={post.created_at ?? undefined}>{time}</time>
                  </>
                ) : null}
                {/* HONEST edited marker — renders ONLY when the server set
                  edited_at (same contract as the feed card's AuthorRow). */}
                {post.edited_at ? (
                  <span aria-label="Edited">· Edited</span>
                ) : null}
              </Typography>
            </Stack>
            {/* The author-only "Post options" kebab (edit) — the SAME self-gating
              PostMenu the feed cards carry, beside this page's bespoke header.
              Renders nothing for a non-author / logged-out viewer. */}
            <PostMenu post={post} />
          </Stack>

          {/* Content as MARKDOWN (S1d) via renderMarkdown — a safe subset parsed to
            React elements, NEVER dangerouslySetInnerHTML. A hostile body (raw HTML
            / javascript: link / markdown image) renders as inert escaped text / a
            sanitized anchor / no <img>. A <div> wrapper (renderMarkdown emits block
            <p>/<ul> elements which cannot nest inside a <p>). */}
          <Box sx={{ overflowWrap: "anywhere" }}>
            {renderMarkdown(post.content)}
          </Box>

          {/* MEDIA — the SAME hardened renderer the feed card uses (PostMedia over
            the server's typed `post.media`): <img referrerPolicy="no-referrer"
            loading="lazy"> for images, a privacy click-to-load nocookie embed for
            youtube. NEVER a raw-`media_urls` <img> and NEVER dangerouslySetInnerHTML.
            Omitted entirely when there is no renderable media. */}
          <MaterialSocialPostMedia media={post.media ?? []} />

          {/* The POST's engagement is the shared Like model — the SAME Like · Repost
            bar the friends feed renders. `hideComment` drops the Comment link (the
            thread + its count already live on this page). Non-optimistic: the
            like/repost counts move only on the refetch (PostActions invalidates the
            whole social arm). The post-level upvote/downvote was retired here. */}
          {/* Like · comment count · Share — the count rides the action row. */}
          <PostActions
            post={post}
            hideComment
            commentSummary={
              <span>
                {/* Pluralize by the SERVER's comment_count: singular only at 1;
                    countLabel owns the number (em-dash when absent). */}
                {countLabel(post.comment_count)}{" "}
                {post.comment_count === 1 ? "comment" : "comments"}
              </span>
            }
          />
        </Stack>
      </Box>

      <Stack
        component="section"
        spacing={1}
        aria-label="Comments"
        sx={{ borderTop: 1, borderColor: "divider", pt: 2, px: { xs: 0.5, sm: 0 } }}
      >
        <Typography component="h2" sx={{ fontSize: "1rem", fontWeight: 700 }}>
          Comments
        </Typography>
        <CommentThread commentsQ={commentsQ} postId={postId ?? ""} />
        <ComposeComment postId={postId ?? ""} />
        {/* Room under the last comment for the pinned field on phones. */}
        <Box aria-hidden sx={{ display: { xs: "block", md: "none" }, height: 64 }} />
      </Stack>
    </Page>
  );
}

/**
 * A COMMENT's first-class Like affordance (#1836) — modeled on the post action
 * bar's Like. The comment converged onto the LIKE model ("Like everywhere"); the
 * ▲ upvote / ▼ downvote pair was retired. aria-pressed mirrors the SERVER's
 * `liked_by_me`; the label toggles "Like" / "Liked"; the count is the server's
 * `like_count` via `actionCountLabel` (a REAL 0 is hidden — label only; an ABSENT
 * count is the em-dash — never a fabricated 0).
 *
 * Each comment mounts its OWN `useLikeComment` mutation, so the button disables
 * while ITS OWN like is pending (per-comment busy scoping): a double-click fires
 * once, and a like on one comment never disables another. NON-OPTIMISTIC — the
 * count moves ONLY after the mutation invalidates and the thread refetches. A
 * rejected like surfaces an inline role="alert" scoped to THIS comment, with the
 * count left at the server's value.
 *
 * The accessible name is the label ("Like" / "Liked") — the glyph is aria-hidden.
 * It is scoped by the comment's <li> container (never inside the post `.post-detail`
 * article), so it never collides with the post's own Like button.
 */
function CommentLikeButton({
  comment,
  postId,
}: {
  comment: CommentResponse;
  postId: string;
}) {
  const likeM = useLikeComment(postId);
  const liked = Boolean(comment.liked_by_me);
  const likeCount = actionCountLabel(comment.like_count);
  return (
    <>
      <Button
        type="button"
        variant="text"
        size="small"
        color={liked ? "primary" : "inherit"}
        aria-pressed={liked}
        disabled={likeM.isPending}
        onClick={() => likeM.mutate({ commentId: comment.comment_id, liked })}
        startIcon={
          liked ? <ThumbUpAltRoundedIcon /> : <ThumbUpAltOutlinedIcon />
        }
      >
        {liked ? "Liked" : "Like"}{" "}
        {likeCount !== null ? (
          <Typography component="span" variant="caption" sx={{ ml: 0.5 }}>
            {likeCount}
          </Typography>
        ) : null}
      </Button>
      {likeM.isError ? (
        <GuardianAwareError error={likeM.error}>
          <Alert severity="error" role="alert">
            We couldn&apos;t record your like. Please try again.
          </Alert>
        </GuardianAwareError>
      ) : null}
    </>
  );
}

/** Thumbs-up glyph for the comment Like (aria-hidden — the label is the name).
 *  Outline at rest; FILLED when liked, mirroring the post action bar's LikeIcon. */
/**
 * The comment thread region — its own query so it loads independently of the
 * post. Loading → role="status"; error → role="alert" (the post above stays);
 * empty → honest "No comments yet"; otherwise one row per server comment, each
 * with its own Like control (#1836). `postId` threads down so each row's
 * `useLikeComment` invalidates the right thread slot.
 */
function CommentThread({
  commentsQ,
  postId,
}: {
  commentsQ: ReturnType<typeof usePostComments>;
  postId: string;
}) {
  if (commentsQ.isLoading) {
    return (
      <Stack role="status" aria-label="Loading comments">
        <CommentSkeleton />
        <CommentSkeleton />
      </Stack>
    );
  }

  if (commentsQ.isError) {
    return (
      <Alert severity="error" role="alert">
        We couldn&apos;t load the comments. Please try again.
      </Alert>
    );
  }

  const comments = commentsQ.data?.items ?? [];
  if (comments.length === 0) {
    return <Typography variant="body2" color="text.secondary">No comments yet.</Typography>;
  }

  return (
    <List disablePadding role="list">
      {comments.map((comment) => (
        <CommentRow
          key={comment.comment_id}
          comment={comment}
          postId={postId}
        />
      ))}
    </List>
  );
}

/**
 * One comment — an initials avatar (mirrors the feed card's author chip) beside a
 * body column: author by display_name (em-dash when absent, never the uid),
 * content as PLAIN TEXT, a RELATIVE time chip (socialTimeLabel — the SAME helper
 * the post byline uses, so a comment and its post read the same format), and its
 * own first-class Like control (#1836). The comment_id / author_id live in keys/data
 * only, never as visible text. The like_count is the server's value — never bumped
 * locally. The avatar is the author's initials (a neutral text-free chip when there
 * is no name) — never a fabricated <img>, never the raw author_id; aria-hidden so the
 * name beside it is the label.
 */
function CommentRow({
  comment,
  postId,
}: {
  comment: CommentResponse;
  postId: string;
}) {
  const { user } = useAuth();
  const [reportOpen, setReportOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const time = socialTimeLabel(comment.created_at);
  const authorName = authorLabel(comment.author_display_name, comment.author_id);
  return (
    <ListItem disableGutters sx={{ alignItems: "flex-start", gap: 1.5, py: 0.75 }}>
      <Avatar aria-hidden sx={{ width: 34, height: 34, fontSize: "0.75rem" }}>
        {authorInitials(comment.author_display_name)}
      </Avatar>
      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">
          <Typography
            component="span"
            sx={{ fontWeight: 700, fontSize: "0.8125rem", color: "text.primary" }}
          >
            {authorName}
          </Typography>
          {authorName !== MISSING ? (
            <span aria-hidden="true">{` ${playerHandleSuffix(comment.author_id)}`}</span>
          ) : null}
          {time ? (
            <>
              <span aria-hidden="true"> · </span>
              <time dateTime={comment.created_at ?? undefined}>{time}</time>
            </>
          ) : null}
        </Typography>
        <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
          {comment.content}
        </Typography>
        {/* Like is the comment's one action (replies aren't a feature yet). */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <CommentLikeButton comment={comment} postId={postId} />
        </Box>
      </Stack>
      {user && user.uid !== comment.author_id ? (
        <>
          <IconButton
            size="small"
            aria-label="Comment options"
            aria-haspopup="menu"
            aria-expanded={menuAnchor ? "true" : undefined}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            sx={{ mt: -0.5 }}
          >
            <MoreHorizRounded fontSize="small" />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                setReportOpen(true);
              }}
            >
              Report comment
            </MenuItem>
          </Menu>
        </>
      ) : null}
      {user && user.uid !== comment.author_id ? (
        <ReportDialog
          open={reportOpen}
          subjectLabel="comment"
          targetId={comment.comment_id}
          targetType="comment"
          onClose={() => setReportOpen(false)}
        />
      ) : null}
    </ListItem>
  );
}

/**
 * The compose-a-comment box (Slice 3). A controlled textarea + Submit. Content is
 * required + trimmed: an empty / whitespace body blocks submit, so useCreateComment
 * is never called. On submit the TRIMMED content is sent as a top-level comment
 * (`createComment(postId, { content })`); on success the textarea clears and the
 * thread REFETCHES (the hook invalidates) — the new comment arrives as the server's
 * truth, never an optimistic phantom row. On failure a role="alert" surfaces and
 * the typed content is RETAINED so the user can retry without re-typing. Submit is
 * disabled while pending so a double-click fires the mutation once.
 */
function ComposeComment({ postId }: { postId: string }) {
  const { user } = useAuth();
  // The same profile the app bar shows, so "you" looks the same everywhere.
  const { data: profile } = useMyProfile(Boolean(user));
  const [content, setContent] = useState("");
  const createComment = useCreateComment(postId);

  const trimmed = content.trim();
  const canSubmit = trimmed.length > 0 && !createComment.isPending;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!trimmed || createComment.isPending) return;
    createComment.mutate(trimmed, {
      onSuccess: () => setContent(""),
    });
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        // Pinned to the bottom: fixed above the phone's tab bar, sticky in the
        // desktop reading column.
        position: { xs: "fixed", md: "sticky" },
        left: { xs: 0 },
        right: { xs: 0 },
        bottom: { xs: "calc(64px + env(safe-area-inset-bottom, 0px))", md: 0 },
        zIndex: 2,
        bgcolor: "background.default",
        px: { xs: 2, md: 0 },
        py: 1,
      }}
    >
      <Stack spacing={1}>
        {createComment.isError ? (
          <GuardianAwareError error={createComment.error}>
            <Alert severity="error" role="alert">
              We couldn&apos;t post your comment. Please try again.
            </Alert>
          </GuardianAwareError>
        ) : null}
        <Stack direction="row" spacing={1} sx={{ alignItems: "flex-end" }}>
        <PlayerAvatar
          name={profile?.display_name ?? null}
          avatarUrl={profile?.avatar ?? null}
          size={34}
        />
        <TextField
          id="compose-comment"
          value={content}
          onChange={(event) => setContent(event.target.value)}
          placeholder="Add a comment…"
          minRows={1}
          maxRows={6}
          multiline
          fullWidth
          size="small"
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "action.hover", pr: 0.5 } }}
          slotProps={{
            htmlInput: { "aria-label": "Add a comment" },
            input: {
              endAdornment: (
                <InputAdornment position="end" sx={{ alignSelf: "flex-end", mb: 1.25 }}>
                  <IconButton type="submit" aria-label="Comment" disabled={!canSubmit} size="small" color="primary">
                    <SendRounded fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        </Stack>
      </Stack>
    </Box>
  );
}
